import { useEffect, useMemo, useState } from "react";
import Table from "../../../../../components/common/Table/Table";
import {
  formatTimestampMDYHMS,
} from "../../../../../utils/date";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import {
  getTuitionAdminEmails,
  getTuitionAdminEmailDetails,
  downloadTuitionAdminAttachment,
} from "../../../../../store/slices/Tuition/tuitionAdminSlice";
import Loader from "../../../../../components/common/Loader";
import downloadOutline from "../../../../../../public/assets/images/downloadOutline.svg";
import { trackButtonClick, trackDocumentUpload } from '../../../../../services/analytics';

const ViewDetailsBtn = ({
  onClick,
  isViewing,
}: {
  onClick?: () => void;
  isViewing?: boolean;
}) => (
  <button
    className={`button whitespace-nowrap flex items-center ${
      isViewing ? "bg-[#003B71]! text-white!" : "-primary "
    }`}
    onClick={isViewing ? undefined : onClick}
  >
    <span>{isViewing ? "Viewing" : "View content"}</span>
  </button>
);

interface EmailTabProps {
  applicationData?: {
    applicationId: string;
    employeeNuId?: string;
  };
}
interface EmailItem {
  commid?: string | number;
  applicationid?: string | number;
  subject?: string;
  emailtype?: string;
  emaildate?: string;
  activeflag?: string;
  createdby?: string;
  createdtime?: string;
  lastupdatedby?: string;
  lastupdatedtime?: string;
}
const Email = ({ applicationData }: EmailTabProps) => {
  const applicationId = applicationData?.applicationId;
  const nuid = applicationData?.employeeNuId;

  // Close email content when applicationId changes
  useEffect(() => {
    setShowEmailContent(false);
    setSelectedCommId(null);
  }, [applicationId]);
  const [showEmailContent, setShowEmailContent] = useState(false);
  const [selectedCommId, setSelectedCommId] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const { emails, emailDetails, loading } = useAppSelector(
    (s) => (s as any).tuitionAdmin
  );

 

  // Centralized attachment download handler
  const handleDownloadAttachment = (
    attachmentId: string,
    attachmentName: string
  ) => {
    console.log(`📊 Analytics: Download attachment clicked - ${attachmentName}`)
    trackButtonClick('Download attachment', 'tuition-admin-emails', { attachmentId })
    if (!applicationId || !attachmentId || !nuid) return;
    trackDocumentUpload('tuition', 'tuition-admin-email-attachment-download', true)
    dispatch(
      downloadTuitionAdminAttachment({
        applicationId,
        documentId: attachmentId,
        fileName: attachmentName,
        nuid,
      })
    );
  };

  useEffect(() => {
    if (applicationId) {
      dispatch(getTuitionAdminEmails({ applicationId, nuid }));
    }
  }, [applicationId, nuid, dispatch]);

  // Document table columns
  const documentColumns = [
    {
      key: "subject",
      label: "Select subject",
      className: "max-w-xs break-words whitespace-normal",
    },
    { key: "emailtype", label: "Type" },
    { key: "emaildate", label: "Sent date" },
    { key: "status", label: "Status" },
    { key: "lastUpdatedBy", label: "Last updated by" },
    { key: "lastUpdatedTime", label: "Last updated time" },
    { key: "action", label: "Action" },
  ];

  // Use shared date utils for consistent formatting

  const emailItems: EmailItem[] = useMemo(() => {
    if (!emails) return [];
    if (Array.isArray(emails)) return emails as EmailItem[];
    if (emails?.items && Array.isArray(emails.items))
      return emails.items as EmailItem[];
    return [];
  }, [emails]);

  const documentRows = useMemo(() => {
    return emailItems.map((item: EmailItem, idx: number) => {
      const commId = String(item.commid ?? idx);
      const row = {
        id: commId,
        subject: item.subject ?? "-",
        emailtype: item.emailtype ?? "-",
        emaildate: item.createdtime
          ? formatTimestampMDYHMS(item.createdtime).split(" ")[0]
          : "-",
        status: item.activeflag === "A" ? "Active" : "Inactive",
        lastUpdatedBy: item.lastupdatedby=== null ?item.createdby: item.lastupdatedby,
        lastUpdatedTime: item.lastupdatedtime === null ? formatTimestampMDYHMS(item.createdtime) : formatTimestampMDYHMS(item.lastupdatedtime),
        action: (
          <ViewDetailsBtn
            isViewing={selectedCommId === commId && showEmailContent}
            onClick={() => {
              setSelectedCommId(commId);
              setShowEmailContent(true);
              if (applicationId) {
                dispatch(
                  getTuitionAdminEmailDetails({ applicationId, commId, nuid })
                );
              }
            }}
          />
        ),
      } as Record<string, any>;
      return row;
    });
  }, [
    emailItems,
    selectedCommId,
    showEmailContent,
    applicationId,
    nuid,
    dispatch,
  ]);

  // Decide what to render for the table area
  let tableArea: JSX.Element;
  if (loading) {
    tableArea = <Loader />;
  } else if (documentRows.length > 0) {
    tableArea = (
      <Table
        columns={documentColumns}
        rows={documentRows}
        showPagination={false}
        caption=" "
      />
    );
  } else {
    tableArea = (
      <p className="p3 -book text-center mt-2.5!">
        No emails found for this application.
      </p>
    );
  }

  return (
    <div className="margin-top-2u">
      <p className="p3 -book mt-5! ml-5!">
        On this page, you can manage documents associated with your application.
        You have two main actions:
      </p>
      <ul className="list-disc ml-5!">
        <li className="ml-5!">
          <b>Review email communications:</b> Select an email from the list to
          view the message content and any attachments. All email records are
          read-only after being sent.
        </li>
        <li className="ml-5!">
          <b>Download attachments:</b> Attachments (if any) are displayed as
          downloadable links. Click on an attachment to download it securely.
        </li>
      </ul>

      <p className="p3 -book mt-5! ml-5!">
        <b>Email communications</b>
      </p>
      <p className="p4 -book ml-5! mt-2!">
        Select an email to view its contents and attachments
      </p>

      {/* Table */}
      <div className="mt-4! ml-5!">{tableArea}</div>

      {/* Email Content Card */}
      {showEmailContent && selectedCommId && (
        <div className="p-4 mt-5! ml-5! rounded-3xl border-2 border-[#D3D6DC]!">
          <div className="ml-8! mt-8!">
            <p className="p3 -book">
              <b>Email Content</b>
            </p>
            <p className="p4 -book mt-2!">
              The email content section is read only. You may download
              associated attachments below.
            </p>
            <div className="w-full pl-4 mt-8! mb-8! ">
              <label
                htmlFor="email"
                className="block text-sm font-medium! text-black! mb-1"
              >
                <b>Email content</b>
              </label>
              <textarea
                autoComplete="on"
                id="courseDescription"
                name="courseDescription"
                className="ds-form__disabled-field bg-gray-100 text-black! text-area_word-count text-area_global -book text-base! "
                data-show-lines="5"
                readOnly
                disabled
                value={emailDetails?.communicationsdtl?.emailContent ?? ""}
              />
            </div>
            {(emailDetails?.attachments ?? []).length > 0 && (
              <>
                <p className="p3 -book">
                  {" "}
                  <b>Attachments</b>
                </p>
                <div className="my-4! flex flex-col gap-2">
                  {(emailDetails?.attachments ?? []).map((att: any) => {
                    const attachmentId = String(att?.attachmentId);
                    const attachmentName = String(att?.attachmentName);
                    return (
                      <button
                        key={attachmentId}
                        type="button"
                        className="text-left p4 -book text-[#0074AD]! hover:underline inline-flex items-center gap-2"
                        onClick={() =>
                          handleDownloadAttachment(attachmentId, attachmentName)
                        }
                        disabled={!applicationId}
                      >
                        <b>{attachmentName}</b>
                        <img
                          src={downloadOutline}
                          alt="download icon"
                          className="w-6! h-6! shrink-0"
                          aria-hidden="true"
                        />
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Email;
